import z from 'zod'
import i18n from '@/translations/i18n'
import { PaginationParamsNode } from '@/types/commons'

export type ListingType = 'airbnb' | 'other'

export const PropertyType = z.enum([
	'aparthotel',
	'apartment',
	'barn',
	'bnb',
	'boat',
	'boutique_hotel',
	'bungalow',
	'cabin',
	'campsite',
	'casa_particular',
	'castle',
	'cave',
	'chalet',
	'condominium',
	'cottage',
	'cycladic_house',
	'dammuso',
	'dome_house',
	'earthhouse',
	'farm_stay',
	'guest_suite',
	'guesthouse',
	'heritage_hotel',
	'holiday_park',
	'hostel',
	'hotel',
	'house',
	'houseboat',
	'hut',
	'igloo',
	'island',
	'kezhan',
	'lighthouse',
	'lodge',
	'loft',
	'minsu',
	'pension',
	'plane',
	'ranch',
	'religious_building',
	'resort',
	'riad',
	'rv',
	'ryokan',
	'serviced_apartment',
	'shepherds_hut',
	'shipping_container',
	'tent',
	'tiny_house',
	'tipi',
	'tower',
	'townhouse',
	'train',
	'treehouse',
	'trullo',
	'villa',
	'windmill',
	'yurt'
])
export type PropertyType = z.infer<typeof PropertyType>

export const SubmissionSource = z.enum([
	'elevate_airbnb_host',
	'elevate_external_host',
	'marketing_manager',
	'luxe_ops_bulk_import',
	'salesforce_sync'
])
export type SubmissionSource = z.infer<typeof SubmissionSource>

export const SubmissionType = z.enum([
	'marketplace_conversion',
	'field_team_nal'
])
export type SubmissionType = z.infer<typeof SubmissionType>

export const SubmissionStatus = z.enum([
	'locked',
	'pending',
	'in_progress',
	'completed',
	'rejected',
	'skipped'
])
export type SubmissionStatus = z.infer<typeof SubmissionStatus>

export const GalleryImage = z.object({
	imageUrl: z.string().nullable(),
	previewUrls: z
		.object({
			extraMedium: z.string().nullable(),
			// 1440px, 1920px and 2560px (the largest Airbnb serves). Null on images
			// that don't come from Airbnb, and nullish until the backend ships them.
			large: z.string().nullish().default(null),
			extraLarge: z.string().nullish().default(null),
			original: z.string().nullish().default(null)
		})
		.nullable()
})
export type GalleryImage = z.infer<typeof GalleryImage>

/**
 * Which Pre-check population a submission sits in: pending_review waits for a
 * vendor/Ops decision, ml_review for the Salesforce ML verdict.
 */
export const PrecheckState = z.enum(['pending_review', 'ml_review'])
export type PrecheckState = z.infer<typeof PrecheckState>

export const QueueListing = z.object({
	id: z.string(),
	hostEmail: z.string(),
	listingCity: z.string().nullable(),
	listingCountryCode: z.string().nullable(),
	propertyAddress: z.string().nullable(),
	precheckStatus: SubmissionStatus,
	propertyType: PropertyType.nullable(),
	notes: z.string().nullable(),
	queueEnteredAt: z.coerce.date().nullish().default(null),
	// Gated by luxe-vv2-precheck-queue-columns: absent or null with the flag
	// off, so the queue keeps parsing until the backend ships the field.
	submissionType: SubmissionType.nullish().default(null),
	// Which Pre-check population this submission belongs to. Null unless
	// luxe-vv2-ml-review or luxe-vv2-vendor-precheck is on, and null on rows
	// that have already left Pre-check.
	precheckState: PrecheckState.nullish().default(null),
	// Where the submission came from, same vocabulary as the other queues.
	// Nullish until the backend ships it on the pre-check endpoint.
	source: SubmissionSource.nullish().default(null),
	// When the submission entered Pre-check, which is what the queue ages rows
	// by — distinct from `submittedAt`, the moment the host submitted. Nullish
	// until the backend ships it.
	daysInQueue: z.number().int().nullish().default(null)
})
export type QueueListing = z.infer<typeof QueueListing>

export const QueueListingsResponse = z.object({
	data: z.array(QueueListing),
	meta: PaginationParamsNode
})
export type QueueListingsResponse = z.infer<typeof QueueListingsResponse>

export const EssentialAmenityType = z.enum([
	'primary_bedroom_ensuite',
	'dining_seating',
	'bathroom_capacity',
	'washer_dryer',
	'kitchen_appliances',
	'air_conditioning',
	'heating'
])
export type EssentialAmenityType = z.infer<typeof EssentialAmenityType>

/**
 * A single amenity the host ticked (or left unticked) on the landing form. The
 * key comes from the landing-form copy rather than a fixed enum, so a new
 * amenity reaches the reviewer without a contract change.
 */
export const GatingAmenityAttestation = z.object({
	key: EssentialAmenityType,
	attested: z.boolean()
})
export type GatingAmenityAttestation = z.infer<typeof GatingAmenityAttestation>

/**
 * Pre-qualification answers a net-new host gave on the landing form, recorded
 * verbatim for the vendor reviewer: the backend applies no pass/fail logic, so
 * these are shown as-is next to the listing's own figures rather than merged
 * into them.
 */
export const GatingAnswers = z.object({
	bedroomCount: z.number().int(),
	// Halves are allowed, so this stays a float unlike the listing's own count.
	bathroomCount: z.number(),
	maxOccupancy: z.number().int(),
	amenitySelfAttestation: z.array(GatingAmenityAttestation)
})
export type GatingAnswers = z.infer<typeof GatingAnswers>

export const QueueListingDetails = QueueListing.extend({
	gallery: z
		.object({
			images: z.array(GalleryImage)
		})
		.nullable(),
	propertyLink: z.string().nullable(),
	description: z.string().nullable(),
	// Gated by luxe-vv2-vendor-precheck: always null with the flag off, and also
	// null when the submission did not come from the landing form.
	gatingAnswers: GatingAnswers.nullish().default(null)
})
export type QueueListingDetails = z.infer<typeof QueueListingDetails>

export const AmenityType = z.enum([
	'pool',
	'kitchen',
	'wifi',
	'parking',
	'whirpool'
])
export type AmenityType = z.infer<typeof AmenityType>

export const FacilitiesKeyNames = z.enum([
	'bedrooms',
	'beds',
	'baths',
	'guests'
])
export type FacilitiesKeyNames = z.infer<typeof FacilitiesKeyNames>

export const Vetter = z.object({
	id: z.number().int(),
	email: z.string(),
	firstName: z.string().nullable(),
	initials: z.string().nullable(),
	lastName: z.string().nullable()
})
export type Vetter = z.infer<typeof Vetter>

export const VisualVettingListing = z.object({
	airbnbListingId: z.string().nullable(),
	id: z.string(),
	location: z.string().nullable(),
	source: SubmissionSource,
	submissionType: SubmissionType.nullable(),
	status: SubmissionStatus,
	listingUrl: z.string().nullable(),
	vetter: Vetter.nullable(),
	// When the submission entered this queue, which the list ages rows by.
	// Nullish until the backend ships it.
	queueEnteredAt: z.string().nullish().default(null),
	// Only set once the vetting is completed. Nullish until the backend ships
	// them.
	score: z.number().nullish().default(null),
	// Gated by luxe-vv2-final-score-display: null with the flag off
	finalScore: z.number().int().nullish().default(null),
	blockersFailedCount: z.number().int().nullish().default(null)
})

export type VisualVettingListing = z.infer<typeof VisualVettingListing>

export const VisualVettingListingsResponse = z.object({
	data: z.array(VisualVettingListing),
	meta: PaginationParamsNode
})

export type VisualVettingListingsResponse = z.infer<
	typeof VisualVettingListingsResponse
>

export const QuestionTier = z.enum([
	'blocker',
	'table_stake',
	'scored',
	'holistic',
	'metadata'
])
export type QuestionTier = z.infer<typeof QuestionTier>

export const ScoreFormQuestion = z.object({
	id: z.string(),
	code: z.string(),
	keyName: z.string(),
	tier: QuestionTier,
	description: z.string(),
	weight: z.number(),
	answerOptions: z.array(z.string()),
	isRequired: z.boolean()
})
export type ScoreFormQuestion = z.infer<typeof ScoreFormQuestion>

export const QuestionAnswer = z.object({
	questionId: z.string(),
	answer: z.string().nullable()
})
export type QuestionAnswer = z.infer<typeof QuestionAnswer>

export const VisualVettingListingDetails = VisualVettingListing.extend({
	photoRepositoryUrl: z.string().nullable(),
	status: SubmissionStatus.optional(),
	bedrooms: z.number().int().nullable(),
	bathrooms: z.number().nullable(),
	beds: z.number().int().nullable(),
	maxGuests: z.number().int().nullable(),
	description: z.string().nullable(),
	isAssignedToCurrentUser: z.boolean(),
	images: z.array(GalleryImage),
	vettingStepStatus: SubmissionStatus,
	questions: z.array(ScoreFormQuestion),
	answers: z.array(QuestionAnswer)
})
export type VisualVettingListingDetails = z.infer<
	typeof VisualVettingListingDetails
>

export const Location = z.object({
	city: z.string().nullable(),
	state: z.string().nullable(),
	countryCode: z.string().nullable(),
	count: z.number()
})
export type Location = z.infer<typeof Location>

export const LocationsResponse = z.object({
	data: z.array(Location),
	meta: PaginationParamsNode
})
export type LocationsResponse = z.infer<typeof LocationsResponse>

export const Reviewer = z.object({
	id: z.number(),
	firstName: z.string().nullable(),
	lastName: z.string().nullable(),
	email: z.string(),
	role: z.string()
})
export type Reviewer = z.infer<typeof Reviewer>

export const ReviewersResponse = z.object({
	data: z.array(Reviewer),
	meta: PaginationParamsNode
})
export type ReviewersResponse = z.infer<typeof ReviewersResponse>

export type LuxeReviewerSection = 'visual_vetting' | 'final_review'
export const ScoreLevelLabel = z.enum([
	'meets_standard',
	'near_miss',
	'significant_gap',
	'not_qualified'
])

export type ScoreLevelLabel = z.infer<typeof ScoreLevelLabel>

export const VisualVettingScoreResponse = z.object({
	score: z.number().nullable(),
	label: ScoreLevelLabel.nullable(),
	hasBlockerFailure: z.boolean(),
	hasTableStakeFailure: z.boolean(),
	outcome: z.string().nullable(),
	failedTableStakes: z.array(z.string()),
	isSubmittable: z.boolean(),
	// Gated by luxe-vv2-final-score-display: null with the flag off. The design
	// score cut by 30% per failed blocking criterion (B1-B3, TS1-TS3), so a
	// design score of 90 with one failed blocker lands at 63.
	finalScore: z.number().nullable(),
	// How many blocking criteria the listing failed — the multiplier's exponent.
	blockersFailedCount: z.number().int().nullable(),
	// The codes of those criteria (B1-B3, TS1-TS3), in the same form as
	// `failedTableStakes`. A superset of it: table stakes plus the blockers.
	failedBlockers: z.array(z.string()).nullable()
})

export type VisualVettingScoreResponse = z.infer<
	typeof VisualVettingScoreResponse
>

export const FinalReviewStatus = z.enum([
	'needs_review',
	'needs_review_mms',
	'passed',
	'failed'
])
export type FinalReviewStatus = z.infer<typeof FinalReviewStatus>

// Submission Activity types
export const SubmissionActivityStatus = z.enum([
	'back_in_review',
	'completed',
	'failed',
	'failed_standards',
	'in_progress',
	'locked',
	'needs_review',
	'needs_review_mms',
	'passed',
	'pending',
	'pre_onboarding',
	'ready_for_inspection',
	'rejected',
	'skipped',
	'submitted',
	'pending_review',
	'accepted',
	'ml_review',
	'ready',
	'blocked',
	'quote_generated'
])
export type SubmissionActivityStatus = z.infer<typeof SubmissionActivityStatus>

export const SubmissionActivityStage = z.enum([
	'host_submission',
	'precheck',
	'visual_vetting',
	'final_review',
	'quote_generation'
])
export type SubmissionActivityStage = z.infer<typeof SubmissionActivityStage>

export const SubmissionActivityActionType = z.enum([
	'submission_created',
	'claimed',
	'assigned',
	'unassigned',
	'status_updated',
	'questionnaire_submitted',
	'decision_made',
	'escalated',
	'second_review_requested'
])
export type SubmissionActivityActionType = z.infer<
	typeof SubmissionActivityActionType
>

export const SubmissionActivityEntry = z.object({
	id: z.string(),
	// null for system events
	userId: z.number().int().nullable().optional(),
	reviewerName: z.string().nullable(),
	stage: SubmissionActivityStage,
	actionType: SubmissionActivityActionType,
	fromStatus: SubmissionActivityStatus.nullable(),
	toStatus: SubmissionActivityStatus.nullable(),
	note: z.string().nullable(),
	createdAt: z.string()
})
export type SubmissionActivityEntry = z.infer<typeof SubmissionActivityEntry>

export const FinalReviewListing = z.object({
	id: z.string(),
	airbnbListingId: z.string().nullable(),
	location: z.string().nullable(),
	source: SubmissionSource,
	submissionType: SubmissionType.nullable(),
	visualVettingScore: z.number().nullable(),
	visualVettingScoreLabel: ScoreLevelLabel.nullable(),
	// Gated by luxe-vv2-final-score-display: null with the flag off
	finalScore: z.number().int().nullable(),
	// How many blocking criteria the listing failed; feeds the score breakdown
	// alongside the design score.
	blockersFailedCount: z.number().int().nullable(),
	reviewStatus: FinalReviewStatus,
	isEscalated: z.boolean(),
	// Gated by luxe-vv2-escalation-reason-in-list: always null with the flag off
	escalationNote: z.string().nullable(),
	assignedReviewerId: z.number().nullable(),
	assignedReviewerName: z.string().nullable(),
	listingUrl: z.string().nullable(),
	vetterFirstName: z.string().nullable(),
	vetterLastName: z.string().nullable(),
	vetterId: z.number().nullable(),
	// When the submission entered this queue, which the list ages rows by.
	// Nullish until the backend ships it.
	queueEnteredAt: z.string().nullish().default(null)
})

export type FinalReviewListing = z.infer<typeof FinalReviewListing>

export const FinalReviewListingDetails = FinalReviewListing.extend({
	vetterFirstName: z.string().nullable().optional(),
	vetterLastName: z.string().nullable().optional(),
	vetterId: z.number().nullable().optional(),

	// Gated by luxe-vv2-scorecard-final-review-comments: empty with the flag off.
	// Activity-log entries carrying a note, ordered oldest first.
	finalReviewComments: z.array(SubmissionActivityEntry).default([]),
	photoRepositoryUrl: z.string().nullable(),
	bedrooms: z.number().int().nullable(),
	bathrooms: z.number().nullable(),
	beds: z.number().int().nullable(),
	maxGuests: z.number().int().nullable(),
	images: z.array(GalleryImage),
	visualVettingScore: z.number().nullable(),
	isEscalated: z.boolean(),
	questions: z.array(
		z.object({
			id: z.string(),
			code: z.string(),
			keyName: z.string(),
			tier: QuestionTier,
			description: z.string(),
			weight: z.number(),
			answerOptions: z.array(z.string()),
			isRequired: z.boolean()
		})
	),
	answers: z.array(
		z.object({
			questionId: z.string(),
			answer: z.string().nullable()
		})
	)
})

export type FinalReviewListingDetails = z.infer<
	typeof FinalReviewListingDetails
>

export type FinalReviewRejectionReason =
	| 'does_not_meet_luxe_design_standards'
	| 'does_not_meet_table_stake_requirements'
	| 'does_not_have_key_luxe_amenities'

export const FinalReviewListingsResponse = z.object({
	data: z.array(FinalReviewListing),
	meta: PaginationParamsNode
})

export type FinalReviewListingsResponse = z.infer<
	typeof FinalReviewListingsResponse
>

export const QuoteGenerationStatus = z.enum([
	'pending_quote',
	'quote_generated'
])
export type QuoteGenerationStatus = z.infer<typeof QuoteGenerationStatus>

/**
 * The queue state driven by Salesforce signals — distinct from `quoteStatus`,
 * which tracks quotes produced inside Luminate. A state whose flag is off reads
 * as "ready", and null means no Salesforce signal was ever recorded, so both
 * render exactly like a ready row.
 */
export const QuoteGenerationState = z.enum([
	'ready',
	'blocked',
	'quote_generated'
])
export type QuoteGenerationState = z.infer<typeof QuoteGenerationState>
export const QuoteGenerationBlocker = z.enum([
	'airbnb_listing_missing',
	'quote_generated_outside_luminate',
	'salesforce_related_listing_missing',
	'host_credentials_missing',
	'submission_type_missing',
	'submission_rejected',
	'blocked_by_salesforce'
])
export type QuoteGenerationBlocker = z.infer<typeof QuoteGenerationBlocker>
export const QuoteGenerationListing = z.object({
	id: z.string(),
	airbnbListingId: z.string().nullable(),
	location: z.string().nullable(),
	source: SubmissionSource,
	submissionType: SubmissionType.nullable(),
	finalReviewer: z
		.object({
			firstName: z.string(),
			lastName: z.string()
		})
		.nullable(),
	// The visual vetter who authored the review. Shown instead of the final
	// reviewer for non-escalated listings once luxe-vv2-final-reviewer-column
	// is on.
	vetter: z
		.object({
			firstName: z.string().nullable(),
			lastName: z.string().nullable()
		})
		.nullable()
		.optional(),
	score: z.number().int().nullable(),
	// Gated by luxe-vv2-final-score-display: null with the flag off
	finalScore: z.number().int().nullable(),
	// How many blocking criteria the listing failed; feeds the score breakdown
	// alongside the design score.
	blockersFailedCount: z.number().int().nullable(),
	isEscalated: z.boolean(),
	quoteStatus: QuoteGenerationStatus,
	// Gated by luxe-vv2-quote-blocked-state / luxe-vv2-quote-generated-state:
	// a state whose flag is off comes back as "ready"
	quoteGenerationState: QuoteGenerationState.nullable().optional(),
	// Non-null only when quoteGenerationState is "blocked"
	quoteBlockReason: z.string().nullable().optional(),
	quoteBlockedAt: z.string().nullable().optional(),
	// False when the backend can't generate a quote (e.g. missing listing
	// info). Nullish until the backend ships it, treated as available.
	canGenerateQuote: z.boolean().nullish().default(true),
	quoteGenerationBlocker: QuoteGenerationBlocker.nullable().optional(),
	listingUrl: z.string().nullable(),
	// When the submission entered this queue, which the list ages rows by.
	// Nullish until the backend ships it.
	queueEnteredAt: z.string().nullish().default(null)
})
export type QuoteGenerationListing = z.infer<typeof QuoteGenerationListing>

export const QuoteGenerationListingsResponse = z.object({
	data: z.array(QuoteGenerationListing),
	meta: PaginationParamsNode
})
export type QuoteGenerationListingsResponse = z.infer<
	typeof QuoteGenerationListingsResponse
>

// Score Form Types
export type VettingQuestion = VisualVettingListingDetails['questions'][number]
export type VettingAnswer = VisualVettingListingDetails['answers'][number]

export const ScoreFormData = z.object({
	answers: z.record(
		z.string(),
		z.string().min(1, { message: i18n.t('errors:field_required') })
	)
})

export type ScoreFormData = z.infer<typeof ScoreFormData>

// MM Submissions types
export const MMSubmissionInventory = z.enum([
	'standard_mp_conversion',
	'standard_nal',
	'branded_residence_mp_conversion',
	'branded_residence_nal'
])
export type MMSubmissionInventory = z.infer<typeof MMSubmissionInventory>

export const MMSubmissionOrigin = z.enum(['airbnb_listing', 'other_listing'])
export type MMSubmissionOrigin = z.infer<typeof MMSubmissionOrigin>

export const MMSubmissionStatus = z.enum([
	'pre_onboarding',
	'failed_standards',
	'ready_for_inspection',
	'back_in_review'
])
export type MMSubmissionStatus = z.infer<typeof MMSubmissionStatus>

// The parking lot status filter: the backend statuses split by the outcome of
// the second review, which the backend tells apart with hadSecondReview
export const MMParkingLotStatusFilter = z.enum([
	'pre_onboarding',
	'pre_onboarding_after_second_review',
	'back_in_review',
	'failed_standards',
	'failed_second_review'
])
export type MMParkingLotStatusFilter = z.infer<typeof MMParkingLotStatusFilter>

export const MMSubmissionAction = z.enum([
	'generate_quote',
	'ask_second_review',
	'see_details',
	'show_activity',
	'copy_quote_link'
])
export type MMSubmissionAction = z.infer<typeof MMSubmissionAction>

export const MMSubmission = z.object({
	id: z.string(),
	airbnbListingId: z.string().nullable(),
	hostEmail: z.string().nullable(),
	location: z.string().nullable(),
	listingInventory: MMSubmissionInventory.nullable(),
	submissionType: SubmissionType.nullable(),
	origin: MMSubmissionOrigin.nullable(),
	score: z.number().nullable(),
	// Gated by luxe-vv2-final-score-display: null with the flag off
	finalScore: z.number().nullable().optional(),
	blockersFailedCount: z.number().int().nullish().default(null),
	mmStatus: MMSubmissionStatus,
	availableActions: z.array(MMSubmissionAction),
	quoteLink: z.string().nullable(),
	// False when the backend can't generate a quote (e.g. missing listing
	// info). Nullish until the backend ships it, treated as available.
	canGenerateQuote: z.boolean().nullish().default(true),
	quoteGenerationBlocker: QuoteGenerationBlocker.nullable().optional(),
	// Only one second review is allowed: once it's had one the backend stops
	// offering ask_second_review. Nullish until the backend ships it.
	hadSecondReview: z.boolean().nullish().default(false),
	createdAt: z.string(),
	// When the submission entered the parking lot, which the list ages rows by.
	// Nullish until the backend ships it.
	queueEnteredAt: z.string().nullish().default(null),
	listingUrl: z.string().nullable().optional(),
	listingName: z.string().nullable(),
	externalHostId: z.string().nullable()
})
export type MMSubmission = z.infer<typeof MMSubmission>

export type SelectedMMListing = Pick<
	MMSubmission,
	'id' | 'hostEmail' | 'airbnbListingId'
>

export const MMSubmissionsResponse = z.object({
	data: z.array(MMSubmission),
	meta: PaginationParamsNode
})
export type MMSubmissionsResponse = z.infer<typeof MMSubmissionsResponse>

// MM Parking Lot types
export const MMParkingLotListingsResponse = z.object({
	data: z.array(MMSubmission),
	meta: PaginationParamsNode
})
export type MMParkingLotListingsResponse = z.infer<
	typeof MMParkingLotListingsResponse
>

// MM Data Overview types
export type MMDataOverviewDatePreset =
	| 'last_6_months'
	| 'last_3_months'
	| 'this_month'

export interface MMDataOverviewParams {
	dateFrom?: string
	dateTo?: string
}

export interface MMDataOverviewStatsItem {
	actionKeyName:
		| 'need_your_action'
		| 'deployed'
		| 'avg_days_to_deploy'
		| 'failed_rejected'
	count: number
	changeValue: number | null
}

export const MMMetricWithTrend = z.object({
	current: z.number(),
	previous: z.number()
})
export type MMMetricWithTrend = z.infer<typeof MMMetricWithTrend>

export const MMDataOverviewPipelineBreakdown = z.object({
	preOnboarding: z.number(),
	failedStandards: z.number(),
	readyForInspection: z.number(),
	backInReview: z.number()
})
export type MMDataOverviewPipelineBreakdown = z.infer<
	typeof MMDataOverviewPipelineBreakdown
>

export const MMDataOverviewInventoryBreakdown = z.object({
	standardMpConversion: z.number(),
	standardNal: z.number(),
	brandedResidenceNal: z.number(),
	brandedResidenceMpConversion: z.number()
})
export type MMDataOverviewInventoryBreakdown = z.infer<
	typeof MMDataOverviewInventoryBreakdown
>

export const MMDataOverviewRecentActivity = z.object({
	quotesGenerated: MMMetricWithTrend,
	secondReviewRequested: MMMetricWithTrend,
	reInspectionNeeded: MMMetricWithTrend,
	listingsSubmitted: MMMetricWithTrend
})
export type MMDataOverviewRecentActivity = z.infer<
	typeof MMDataOverviewRecentActivity
>

export const VisualVettingVolumeRow = z.object({
	userId: z.number(),
	email: z.string(),
	firstName: z.string().nullable(),
	lastName: z.string().nullable(),
	role: z.string(),
	values: z.array(z.number()),
	total: z.number()
})
export type VisualVettingVolumeRow = z.infer<typeof VisualVettingVolumeRow>

export const VisualVettingVolumeResponse = z.object({
	granularity: z.enum(['daily', 'weekly', 'monthly']),
	from: z.string(),
	to: z.string(),
	buckets: z.array(z.string()),
	rows: z.array(VisualVettingVolumeRow),
	totalsByBucket: z.array(z.number()),
	grandTotal: z.number()
})
export type VisualVettingVolumeResponse = z.infer<
	typeof VisualVettingVolumeResponse
>

export const VisualVettingAccuracyRow = z.object({
	userId: z.number(),
	email: z.string(),
	firstName: z.string().nullable(),
	lastName: z.string().nullable(),
	role: z.string(),
	values: z.array(z.number().nullable())
})
export type VisualVettingAccuracyRow = z.infer<typeof VisualVettingAccuracyRow>

export const VisualVettingAccuracyResponse = z.object({
	granularity: z.enum(['daily', 'weekly', 'monthly']),
	from: z.string(),
	to: z.string(),
	buckets: z.array(z.string()),
	rows: z.array(VisualVettingAccuracyRow),
	totalsByBucket: z.array(z.number().nullable())
})
export type VisualVettingAccuracyResponse = z.infer<
	typeof VisualVettingAccuracyResponse
>

export const MMDataOverview = z.object({
	stalledCount: z.number(),
	needYourAction: z.number(),
	deployed: MMMetricWithTrend,
	avgDaysToDeploy: MMMetricWithTrend,
	failedRejected: MMMetricWithTrend,
	pipelineBreakdown: MMDataOverviewPipelineBreakdown,
	inventoryBreakdown: MMDataOverviewInventoryBreakdown,
	recentActivity: MMDataOverviewRecentActivity
})
export type MMDataOverview = z.infer<typeof MMDataOverview>

export const MMSubmissionDetails = MMSubmission.extend({
	origin: MMSubmissionOrigin.nullable().optional(),
	listingInventory: MMSubmissionInventory.nullable().optional(),
	submissionType: SubmissionType.nullable().optional(),

	location: z.string().nullable().optional(),
	listingName: z.string().nullable(),
	propertyAddress: z.string().nullable(),
	propertyType: z.string().nullable(),
	parentSubmissionId: z.string().nullable(),
	bedrooms: z.number().nullable(),
	bathrooms: z.number().nullable(),
	beds: z.number().nullable(),
	description: z.string().nullable(),
	maxGuests: z.number().nullable().optional(),
	images: GalleryImage.array(),
	questions: z.array(ScoreFormQuestion).optional(),
	answers: z.array(QuestionAnswer).optional(),
	photoRepositoryUrl: z.string().nullable(),

	// Gated by luxe-vv2-scorecard-final-review-comments: empty with the flag off.
	// Activity-log entries carrying a note, ordered oldest first.
	finalReviewComments: z.array(SubmissionActivityEntry).default([])
})
export type MMSubmissionDetails = z.infer<typeof MMSubmissionDetails>

// CSV Uploads
export const CsvUploadStatus = z.enum([
	'pending_upload',
	'uploaded',
	'processing',
	'completed',
	'failed'
])
export type CsvUploadStatus = z.infer<typeof CsvUploadStatus>

export const CsvUpload = z.object({
	id: z.string(),
	fileName: z.string(),
	status: CsvUploadStatus,
	uploadedByUserId: z.number(),
	uploadedByUserName: z.string().nullable(),
	totalRows: z.number(),
	failedRows: z.number(),
	processedRows: z.number(),
	uploadedAt: z.string().nullable()
})
export type CsvUpload = z.infer<typeof CsvUpload>

export const CsvUploadsResponse = z.object({
	data: z.array(CsvUpload),
	meta: PaginationParamsNode
})
export type CsvUploadsResponse = z.infer<typeof CsvUploadsResponse>

export const LUXE_VV2_FLAG_KEYS = [
	'luxe-vv2-escalation-reason-in-list',
	'luxe-vv2-scorecard-final-review-comments',
	'luxe-vv2-final-reviewer-column',
	'luxe-vv2-post-escalation-redirect',
	'luxe-vv2-unassigned-filter',
	'luxe-vv2-acc-active-assignments',
	'luxe-vv2-precheck-queue-columns',
	'luxe-vv2-quote-gen-transition-fix',
	'luxe-vv2-ts4-unsure',
	'luxe-vv2-ts7-removal',
	'luxe-vv2-sf-inbound-sync',
	'luxe-vv2-sf-submission-autocreate',
	'luxe-vv2-ml-review',
	'luxe-vv2-vendor-precheck',
	'luxe-vv2-final-score-routing',
	'luxe-vv2-final-score-display',
	'luxe-vv2-sf-auto-upload',
	'luxe-vv2-quote-blocked-state',
	'luxe-vv2-quote-generated-state',
	'luxe-vv2-distribution-listing-source'
] as const

export type LuxeFeatureFlagKey = (typeof LUXE_VV2_FLAG_KEYS)[number]

/**
 * The backend is the single source of flag state: unknown keys are ignored and
 * a missing key is treated as off, so a flag only ever releases a piece when
 * the backend says so.
 */
export const LuxeFeatureFlagsResponse = z.object({
	flags: z.record(z.string(), z.boolean()),
	/**
	 * Whether these are the caller's own flags and the X-Luxe-Vv2-Flags header is
	 * honoured. False means they are the environment's and there is nothing to
	 * steer, so no flag picker should be rendered.
	 */
	teamOverrideActive: z.boolean().optional().default(false)
})
export type LuxeFeatureFlagsResponse = z.infer<typeof LuxeFeatureFlagsResponse>
